import {
  forwardRef,
  type InputHTMLAttributes,
  type ReactNode,
  useEffect,
  useId,
  useImperativeHandle,
  useRef,
} from 'react';
import { Check, Minus } from 'lucide-react';

/**
 * Checkbox — Workflow 3.0 (Figma „08.3 · Selection & status").
 *
 * Box rysowany własnoręcznie (20×20px, promień 4px) zamiast natywnego
 * podkolorowanego inputu (poprzednio 16px, brak własnej ikony/indeterminate,
 * wygląd zależny od OS). Input zostaje prawdziwym `<input type="checkbox">`
 * (a11y, klawiatura, formularze) — tylko wizualnie ukryty (`appearance-none`
 * + rozmiar 0, ikonki jako bezpośrednie sibling'i sterowane czystym CSS
 * przez `peer-checked:` / `peer-indeterminate:` — zero JS do synchronizacji
 * wyglądu, działa identycznie w trybie controlled i uncontrolled).
 */

export interface CheckboxProps extends Omit<
  InputHTMLAttributes<HTMLInputElement>,
  'type' | 'size'
> {
  label?: ReactNode;
  hint?: string;
  error?: string;
  /** Stan pośredni (lucide `Minus`) — ustawiany przez DOM property `indeterminate`, nie atrybut HTML. */
  indeterminate?: boolean;
  containerClassName?: string;
}

export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(function Checkbox(
  {
    label,
    hint,
    error,
    indeterminate = false,
    id,
    className,
    containerClassName,
    'aria-describedby': describedByProp,
    ...rest
  },
  ref,
) {
  const autoId = useId();
  const inputId = id ?? autoId;
  const hintId = hint ? `${inputId}-hint` : undefined;
  const errorId = error ? `${inputId}-error` : undefined;
  const describedBy = [describedByProp, errorId, hintId].filter(Boolean).join(' ') || undefined;

  const innerRef = useRef<HTMLInputElement>(null);
  useImperativeHandle(ref, () => innerRef.current as HTMLInputElement, []);
  useEffect(() => {
    if (innerRef.current) {
      innerRef.current.indeterminate = indeterminate;
    }
  }, [indeterminate]);

  return (
    <div className={['flex flex-col gap-1', containerClassName ?? ''].join(' ')}>
      <label htmlFor={inputId} className="inline-flex items-start gap-2 cursor-pointer">
        <span className="relative mt-0.5 inline-flex h-5 w-5 shrink-0">
          <input
            ref={innerRef}
            id={inputId}
            type="checkbox"
            aria-invalid={error ? true : undefined}
            aria-describedby={describedBy}
            className={[
              'peer h-5 w-5 shrink-0 appearance-none rounded border bg-bg-primary transition-colors',
              'focus:outline-none focus-visible:ring-[3px] focus-visible:ring-accent-600',
              'disabled:cursor-not-allowed disabled:opacity-50',
              error
                ? 'border-[rgb(var(--color-error))] checked:border-[rgb(var(--color-error))] checked:bg-[rgb(var(--color-error))] focus-visible:ring-[rgb(var(--color-error))]'
                : 'border-border-secondary hover:border-border-primary checked:border-accent-600 checked:bg-accent-600 indeterminate:border-accent-600 indeterminate:bg-accent-600',
              className ?? '',
            ].join(' ')}
            {...rest}
          />
          <Check
            aria-hidden="true"
            className="pointer-events-none absolute inset-0 m-auto h-3.5 w-3.5 text-white opacity-0 peer-checked:opacity-100 peer-indeterminate:opacity-0"
          />
          <Minus
            aria-hidden="true"
            className="pointer-events-none absolute inset-0 m-auto h-3.5 w-3.5 text-white opacity-0 peer-indeterminate:opacity-100"
          />
        </span>
        {label && <span className="text-sm text-text-primary select-none">{label}</span>}
      </label>
      {hint && !error && (
        <span id={hintId} className="text-xs text-text-muted pl-7">
          {hint}
        </span>
      )}
      {error && (
        <span id={errorId} className="text-xs text-red-600 pl-7" role="alert">
          {error}
        </span>
      )}
    </div>
  );
});
